import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(null);
const STORAGE_KEY = 'whe2_web_user';

const normalizeStoredUser = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const uid = raw.uid || raw.id || '';
  const email = raw.email || '';
  const displayName = raw.displayName || raw.name || '';
  const photoURL = raw.photoURL || raw.avatar || '';

  if (!uid && !email && !displayName) return null;

  return {
    uid: uid || email || 'web-user',
    email,
    displayName,
    photoURL
  };
};

const readStoredUser = () => {
  if (typeof window === 'undefined') return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    return normalizeStoredUser(parsed);
  } catch (_err) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context) return context;
  return {
    user: null,
    profile: null,
    photoURL: '',
    loading: false,
    error: null,
    signInWithGoogle: async () => false,
    signInWithApple: async () => false,
    logout: async () => {}
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser());

  useEffect(() => {
    const handleStorage = () => setUser(readStoredUser());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const logout = async () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    setUser(null);
  };

  const value = useMemo(() => ({
    user,
    profile: user ? {
      uid: user.uid,
      name: user.displayName || '',
      email: user.email || '',
      photoURL: user.photoURL || ''
    } : null,
    photoURL: user?.photoURL || '',
    loading: false,
    error: null,
    signInWithGoogle: async () => false,
    signInWithApple: async () => false,
    logout
  }), [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
